import { HomeSinisterUrgentQuestionsFeatureFlags } from '@models/mmobile';
import { PolicyOtherHolder } from '@models/policy-other';
import { User } from '@models/user';
import { ScheduleInput } from '@utils/utils';
import * as moment from 'moment';
import { Address } from './address';
import { Document } from './policy';
import { PolicyHome } from './policy-home';

export type PatrimonialDataChangeEnabledConfigKeys = 'holder' | 'postalDelivery';

export type PatrimonialDataType =
  | 'DESCRIPTION'
  | 'REPAIR'
  | 'DAMAGES'
  | 'CAUSES'
  | 'CAUSATIVE'
  | 'INJURED'
  | 'BILL';

export type AnswerValue = 'YES' | 'NO' | 'UNKNOWN';
export type Priority = 'NORMAL' | 'URGENT';

export interface PatrimonialDataChangeEnabledConfig {
  companyId: string;
  department: string[];
  online: boolean;
}

export interface ProfessionalVisit {
  inHouse?: boolean;
  timeRange?: FromToTime;
  selectedActivities?: SinisterDamage[];
}
export interface FromToTime {
  initTime: string;
  finishTime: string;
}
export interface PatrimonialSinister {
  type: HomeSinisterType;
  priority: Priority;
  policyCommercialProductCode: string;
  occurrenceDate: string; // ?
  occurrenceAddress: Address;
  contactData: ContactData;
  description: string;
  damageDescription?: string;
  damageDescriptionResume?: string;
  inHouse?: boolean;
  initTime?: string;
  finishTime?: string;
  attender?: Implicated;
}

export type ShowInTemplate = {
  [key in keyof UrgentQuestions]: boolean;
};

export interface UrgentQuestions {
  generousLeak: boolean;
  stopcockClosed: boolean;
  accessAvailable: boolean;
  intrudersRisk: boolean;
  powerCut: PowerCut;
  urgentRepairerNeeded: boolean;
  cutsDanger: boolean;
  unprotected: boolean;
  glassCeramicUnoperative: boolean;
  wcRelated: boolean;
  noReplacement: boolean;
  accessUnavailable: boolean;
}

export type PowerCut = 'YES' | 'NO' | 'IN_SOME_PLACES';

export interface ContactData {
  email: string;
  phone: string;
  mobilePhone: string;
  address: Address;
}

export interface Implicated {
  name: string;
  surname1: string;
  surname2: string;
  documentNumber?: string;
  address?: Address;
  phone: string;
  insuranceCompany?: string;
  policyNumber?: string;
  documentType?: string;
  description?: string;
}

export interface RepairData {
  repairedInPart: boolean;
  repairNeeded: boolean;
  repairerNeeded: boolean;
  repairedDamage: boolean;
  repairDescription: string;
  repairAmount: number;
  bills: Document[];
}

export interface AvailableSinister {
  name: string;
  selected?: boolean;
  description: string;
  iconSrc: string;
  type: HomeSinisterType;
  online?: boolean;
  validByDate?: boolean;
  oldTypeCode: string;
  orderNum?: number;
  allowBricomercio?: boolean;
}

export interface Intervention {
  availableInterventions: number;
  usedInterventions: number;
}

export interface SinisterDamage {
  code: string;
  name: string;
  typeCode?: string;
  checked?: boolean;
  derivedWorks?: SinisterDamage[];
  group?: string;
}

export interface Brand {
  code: string;
  name: string;
}

export interface ObjectDamaged {
  code: string;
  name: string;
  objectTypes?: SinisterDamage[];
  objectBrands?: SinisterDamage[];
  typeSelected?: SinisterDamage;
  brandSelected?: SinisterDamage;
  otherType?: string;
  description?: string;
  checked?: boolean;
}

export interface BreakGlassReport extends PatrimonialSinister {
  type: HomeSinisterType.GLASS_BREAK;
  thirdCausative: boolean;
  thirdCausativeData: boolean;
  causatives: Implicated[];
  selectedDamages: SinisterDamage[];
  repairData: RepairData;
}

export interface ApplianceRepairReport extends PatrimonialSinister {
  type: HomeSinisterType.APPLIANCE_REPAIR;
  selectedDamage: SinisterDamage;
  selectedBrand: Brand;
  otherBrandDescription: string;
  applianceModel: string;
  applianceAntiquity: string;
  warranty: AnswerValue;
}

export interface WaterDamageReport extends PatrimonialSinister {
  type: HomeSinisterType.WATER;
  thirdCausative: boolean;
  thirdCausativeData: boolean;
  causatives: Implicated[];
  thirdInjured: boolean;
  injureds: Implicated[];
  selectedDamages: SinisterDamage[];
  repairData: RepairData;
}

export interface AtmosphericPhenomenaReport extends PatrimonialSinister {
  type: HomeSinisterType.ATMOSPHERIC;
  selectedDamageCause: SinisterDamage;
  selectedLeakagePlace: SinisterDamage;
  otherLeakageDescription: string;
  selectedHousingRegime: SinisterDamage;
  selectedDamageLocations: SinisterDamage[];
  moreDamagesInAYear: boolean;
  initialDate: string;
  finalDate: string;
  repairData: RepairData;
}

export interface HomeDiyReport extends PatrimonialSinister {
  type: HomeSinisterType.DIY;
  selectedDamage: SinisterDamage;
  selectedDamages: SinisterDamage[];
  derivedDamages: AnswerValue;
}

export interface AllAccidentalRiskReport extends PatrimonialSinister {
  type: HomeSinisterType.ALL_RISK;
  objectDamagedDescription: string;
  damageLocation: SinisterDamage;
  objectDamages: ObjectDamaged[];
  repairData: RepairData;
}

export interface ElectricDamagesReport extends PatrimonialSinister {
  type: HomeSinisterType.ELECTRICAL;
  selectedDamages: SinisterDamage[];
  selectedBrand: Brand;
  otherBrandDescription: string;
  applianceModel: string;
  selectedElectricCompany: SinisterDamage;
  electricCompanyName: string;
  electricCompanyContractNumber: string;
  hoursWithoutService: number;
  nearlyWorks: boolean;
  neighborsAffected: AnswerValue;
  repairData: RepairData;
}

export interface TheftReport extends PatrimonialSinister {
  type: HomeSinisterType.THEFT;
  selectedDamages: SinisterDamage[];
  selectedActivities?: SinisterDamage[];
  existsPolicyReport: boolean;
  policyReport: Document;
  existsObjectBills: boolean;
  objectBills: Document[];
  existsMaterialDamages: boolean;
  materialDamagesDescription: string;
  repairData: RepairData;
}

export interface KeysLossReport extends PatrimonialSinister {
  type: HomeSinisterType.KEY_LOSS;
  repairData: RepairData;
  attender: Implicated;
  attendedByInsured: boolean;
}

export interface TheftAttemptReport extends PatrimonialSinister {
  type: HomeSinisterType.THEFT_ATTEMPT;
  selectedDamages: SinisterDamage[];
  repairData: RepairData;
}

export interface CivilLiabilityReport extends PatrimonialSinister {
  type: HomeSinisterType.CIVIL_LIABILITY;
  existsWaterDamages: boolean;
  thirdInjured: boolean;
  injureds: Implicated[];
}

export interface LegalDefenseReport extends PatrimonialSinister {
  type: HomeSinisterType.LEGAL_ADVICE;
  ownClaim: boolean;
  causatives: Implicated[];
  thirdClaim: boolean;
  injureds: Implicated[];
  documents: Document[];
}
export interface PatrimonialIdentification {
  documentNumber: string;
  phoneNumber: string;
  iban?: string;
}

export interface PatrimonialValidation {
  businessId: string;
  token: string;
}

export interface PublicPatrimonialClient {
  address: Address;
  documentNumber: string;
  documentType: string;
  email: string;
  phone: string;
  name: string;
  surname1: string;
  surname2: string;
  userId?: string;
}

export interface PatrimonialUserValid {
  homeOtherPolicies: PolicyHome[];
  patrimonialsJwtToken: string;
  patrimonialsClient: PublicPatrimonialClient;
}
export interface DayHourRange {
  date: moment.Moment;
  startDay?: number;
  lastDay?: number;
  startingHour?: number;
  endingHour?: number;
}

export enum PublicPatrimonialsErrors {
  PHONE_INVALID = 'PATRIMONIALS.PHONE.VALIDATION.PHONE_INVALID',
  RIC_CLIENT_NOT_FOUND = 'PATRIMONIALS.RIC.CLIENT.NOT_FOUND',
  RIC_PHONE_INVALID = 'PATRIMONIALS.RIC.VALIDATION.PHONE_INVALID',
  RIC_CLIENT_DUPLICATED = 'PATRIMONIALS.RIC.VALIDATION.CLIENT_DUPLICATED',
  RIC_CLIENT_NOT_FOUND_IBAN = 'PATRIMONIALS.RIC.CLIENT.NOT_FOUND_IBAN',
  RIC_CLIENT_DUPLICATED_IBAN = 'PATRIMONIALS.RIC.VALIDATION.CLIENT_DUPLICATED_IBAN',
  RIC_CLIENT_NO_POLICIES = 'PATRIMONIALS.RIC.CLIENT.NO_POLICIES',
  MMOBILE_PROVIDER_ERROR = '301'
}

export enum PatrimonialsRoute {
  PUBLIC_ROUTE = 'pwa-patrimonials',
  REPORT_PROCCESS_ROUTE = 'patrimonial-sinister'
}

export enum HomeSinisterType {
  WATER = 'A',
  DIY = 'BH',
  GLASS_BREAK = 'C',
  ELECTRICAL = 'DE',
  CIVIL_LIABILITY = 'DT',
  KEY_LOSS = 'ELL',
  ATMOSPHERIC = 'FA',
  THEFT_ATTEMPT = 'IR',
  LEGAL_ADVICE = 'JUR',
  THEFT = 'R',
  APPLIANCE_REPAIR = 'RE',
  ALL_RISK = 'TRA',
  COMPUTER_ASSISTANCE = 'AI'
}

export const enum SinisterDamageCode {
  DOOR_AND_WINDOW_GLASS = '0101',
  FURNITURE_AND_MIRROR_GLASS = '0102',
  SKYLIGHT_GLASS = '0103',
  GLASS_CERAMIC = '0104',
  FIXED_MIRRORS = '0105',
  METHACRYLATE = '0106',
  MARBLE_COUNTERTOPS = '0107',
  MARBLE_TABLES = '0108',
  SANITARY_EQUIPMENT = '0110'
}

export enum WorkingDaysAndHours {
  STARTING_HOUR = 8,
  ENDING_HOUR = 17,
  STARTING_DAY = 1,
  ENDING_DAY = 5
}

export const SinisterFeature = new Map<
  HomeSinisterType,
  HomeSinisterUrgentQuestionsFeatureFlags
>([
  [
    HomeSinisterType.GLASS_BREAK,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_BREAKS
  ],
  [
    HomeSinisterType.KEY_LOSS,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_LOSTKEYS
  ],
  [
    HomeSinisterType.ELECTRICAL,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_ELECTRICALDAMAGE
  ],
  [
    HomeSinisterType.THEFT,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_THEFT
  ],
  [
    HomeSinisterType.THEFT_ATTEMPT,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_THEFTATTEMPT
  ],
  [
    HomeSinisterType.WATER,
    HomeSinisterUrgentQuestionsFeatureFlags.PATRIMONIAL_URGENT_QUESTIONS_WATERDAMAGE
  ]
]);

export const isGlassReport = (
  sinister: PatrimonialSinister
): sinister is BreakGlassReport =>
  sinister.type === HomeSinisterType.GLASS_BREAK;

export const isApplianceReport = (
  sinister: PatrimonialSinister
): sinister is ApplianceRepairReport =>
  sinister.type === HomeSinisterType.APPLIANCE_REPAIR;

export const isWaterDamageReport = (
  sinister: PatrimonialSinister
): sinister is WaterDamageReport => sinister.type === HomeSinisterType.WATER;

export const isAtmosphericPhenomenaReport = (
  sinister: PatrimonialSinister
): sinister is AtmosphericPhenomenaReport =>
  sinister.type === HomeSinisterType.ATMOSPHERIC;

export const isHomeDiyReport = (
  sinister: PatrimonialSinister
): sinister is HomeDiyReport => sinister.type === HomeSinisterType.DIY;

export const isAllAccidentalRiskReport = (
  sinister: PatrimonialSinister
): sinister is AllAccidentalRiskReport =>
  sinister.type === HomeSinisterType.ALL_RISK;

export const isElectricDamagesReport = (
  sinister: PatrimonialSinister
): sinister is ElectricDamagesReport =>
  sinister.type === HomeSinisterType.ELECTRICAL;

export const isTheftReport = (
  sinister: PatrimonialSinister
): sinister is TheftReport => sinister.type === HomeSinisterType.THEFT;

export const isKeysLossReport = (
  sinister: PatrimonialSinister
): sinister is KeysLossReport => sinister.type === HomeSinisterType.KEY_LOSS;

export const isTheftAttemptReport = (
  sinister: PatrimonialSinister
): sinister is TheftAttemptReport =>
  sinister.type === HomeSinisterType.THEFT_ATTEMPT;

export const isCivilLiabilityReport = (
  sinister: PatrimonialSinister
): sinister is CivilLiabilityReport =>
  sinister.type === HomeSinisterType.CIVIL_LIABILITY;

export const isLegalDefenseReport = (
  sinister: PatrimonialSinister
): sinister is LegalDefenseReport =>
  sinister.type === HomeSinisterType.LEGAL_ADVICE;

export const damagesWithoutProfessionalOnDuty = [
  SinisterDamageCode.GLASS_CERAMIC,
  SinisterDamageCode.METHACRYLATE,
  SinisterDamageCode.MARBLE_COUNTERTOPS,
  SinisterDamageCode.MARBLE_TABLES,
  SinisterDamageCode.SANITARY_EQUIPMENT
];

export const professionalVisitSchedule: ScheduleInput[] = [
  {
    from: {
      hour: 8
    },
    to: {
      hour: 11
    }
  },
  {
    from: {
      hour: 9
    },
    to: {
      hour: 12
    }
  },
  {
    from: {
      hour: 10
    },
    to: {
      hour: 13
    }
  },
  {
    from: {
      hour: 11
    },
    to: {
      hour: 14
    }
  },
  {
    from: {
      hour: 12
    },
    to: {
      hour: 15
    }
  },
  {
    from: {
      hour: 13
    },
    to: {
      hour: 16
    }
  },
  {
    from: {
      hour: 14
    },
    to: {
      hour: 17
    }
  },
  {
    from: {
      hour: 15
    },
    to: {
      hour: 18
    }
  },
  {
    from: {
      hour: 16
    },
    to: {
      hour: 19
    }
  },
  {
    from: {
      hour: 17
    },
    to: {
      hour: 20
    }
  }
];

export type HolderDataPathType = 'phone' | 'address';
export enum HolderDataPath {
  PHONE = 'phone',
  ADDRESS = 'address'
}

export enum PatrimonialChangeDataSpinnerId {
  SEND_CORRESPONDENCE = 'send-correspondence',
  HOLDER_RESIDENCE = 'holder-residence',
}

export const PatrimonialDataChangeCustomConfigKey = 'policyDataChange';

export interface HolderDataResponse {
  code: string;
  description: string;
  dataId: string;
}

export interface PatrimonialAddress extends Address {
  postalCode: string;
  province: string;
  location: string;
  viaType: string;
  viaName: string;
  number: string;
  complement: string;
  door: string;
  flat: string;
  portal: string;
  stairs: string;
  internationalAddress: string;
}

export interface PatrimonialDataChangeState {
  mailingAddress: { [policyId: string]: Partial<MailingAddress> };
  holderResidenceAddress: { [policyId: string]: Partial<HolderResidenceAddress> };
}

export type MailingAddress = Pick<User, 'name'> & Pick<PolicyOtherHolder, 'residence'> & Address;
export type HolderResidenceAddress =
  Pick<User, 'phone'> &
  Pick<PolicyOtherHolder, 'residence'>
  & Address
  & { phoneUpdated: boolean, residenceUpdated: boolean };

export const POLICES_PATRIMONIAL_DATA_CHANGE_AVAILABLE = ['home', 'accident', 'other', 'death'];

